import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { AUTH_ERRORS } from '../components/auth/authConfig';
import '../assets/styles/Login.css';

const ResetPassword = () => {
  const [step, setStep] = useState(1); // 1: Request code, 2: Reset with code
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { requestPasswordReset, confirmPasswordReset } = useAuth();

  // Handle the initial password reset request
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess('Verification code sent! Check your email and enter the code below.');
      setStep(2);
    } catch (err) {
      console.error('Error requesting password reset:', err);
      
      if (err.message.includes('LimitExceededException')) {
        setError('Too many password reset attempts. Please try again later.');
      } else if (err.message.includes('UserNotFoundException')) {
        setError('No account found with this email address.');
      } else if (err.message.includes('network')) {
        setError(AUTH_ERRORS.network);
      } else {
        setError(AUTH_ERRORS.generalError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset confirmation with code
  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError(AUTH_ERRORS.passwordMismatch);
      setIsLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(email, verificationCode, newPassword);
      setSuccess('Password has been reset successfully!');
      
      // After a short delay, navigate to login page
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Password reset successful. Please log in with your new password.' }
        });
      }, 2000);
    } catch (err) {
      console.error('Error confirming password reset:', err);
      
      if (err.message.includes('invalid confirmation code')) {
        setError(AUTH_ERRORS.invalidCode);
      } else if (err.message.includes('Password does not conform')) {
        setError(AUTH_ERRORS.passwordRequirements);
      } else if (err.message.includes('network')) {
        setError(AUTH_ERRORS.network);
      } else {
        setError(AUTH_ERRORS.generalError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>AI Maths Tutor</h2>
        <h3>Reset Password</h3>

        {step === 1 ? (
          <form onSubmit={handleRequestReset}>
            <p className="info-message">
              Enter your email address to receive a verification code.
            </p>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            
            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Verification Code'}
            </button>
            
            <div className="back-link">
              <Link to="/login">Back to Login</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmReset}>
            <p className="info-message">
              Enter the verification code from your email and set a new password.
            </p>
            
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="new-password">New Password</label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
              <small className="password-hint">
                Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            
            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
            
            <div className="form-links">
              <button
                type="button"
                className="text-button"
                onClick={() => setStep(1)}
              >
                Resend Code
              </button>
              <Link to="/login">Back to Login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
